import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import './EtatInventory.css';
import EtatCgmModal from './EtatCgmModal';
import { getActiveCaisseYear, setActiveCaisseYear, buildCaisseYearOptions, syncActiveCaisseYearWithCalendar, subscribeCaisseYear } from '../utils/caisseActiveYear';
import { recordEtatCgmPrint } from '../utils/recordEtatCgmPrint';
import { authJsonHeaders } from '../utils/authHeaders';

const isHonoraireRecu = (libelle) => {
    if (!libelle) return false;
    const s = String(libelle).toUpperCase();
    return (
        s.includes('HONORAIRES REÇU') ||
        s.includes('HONORAIRES RECU') ||
        s.includes('HONORAIRES RE') ||
        s.includes('HONORAIRES PLATEFORME TEJ') ||
        s === 'HONORAIRES PLATEFORME TEJ'
    );
};

const formatMontant = (montant) => {
    if (montant === null || montant === undefined) return '0,000 TND';
    const num = typeof montant === 'number' ? montant : parseFloat(String(montant).replace(/,/g, '.').replace(/\s/g, '')) || 0;
    const isNeg = num < 0;
    const abs = Math.abs(num);
    const parts = abs.toFixed(3).split('.');
    const intFormatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${isNeg ? '-' : ''}${intFormatted},${parts[1] || '000'} TND`;
};

const MONTH_NAMES = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export default function AllEtatBureauPage() {
    const { activeCabinet } = useAuth();
    const cabinetLabel = activeCabinet?.is_default || Number(activeCabinet?.id) === 1 ? 'CGM1' : 'CGM2';
    const [selectedYear, setSelectedYear] = useState(() => {
        syncActiveCaisseYearWithCalendar();
        return getActiveCaisseYear();
    });
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [tab, setTab] = useState('annee');
    const [jourMode, setJourMode] = useState('date');
    const [showModal, setShowModal] = useState(false);

    const [summary, setSummary] = useState({
        honoraires: 0,
        depenses: 0,
        reste: 0,
        loading: true,
    });

    useEffect(() => {
        return subscribeCaisseYear((y) => setSelectedYear(y));
    }, []);

    const onYearChange = (y) => {
        setSelectedYear(setActiveCaisseYear(y));
    };

    const getPeriodLabel = () => {
        if (tab === 'annee') return `Année ${selectedYear}`;
        if (tab === 'mois') return `${MONTH_NAMES[selectedMonth - 1] || selectedMonth} ${selectedYear}`;
        if (tab === 'jour') {
            if (jourMode === 'date') return `Jour ${selectedDate ? new Date(selectedDate).toLocaleDateString('fr-FR') : ''}`;
            return `Période du ${dateDebut || '...'} au ${dateFin || '...'}`;
        }
        return '';
    };

    const fetchSummary = useCallback(async () => {
        if (tab !== 'annee' || !selectedYear) return;
        try {
            setSummary(prev => ({ ...prev, loading: true }));
            const qs = `?annee=${selectedYear}`;

            const [honoRes, depRes, retRes] = await Promise.all([
                fetch(`/api/honoraires${qs ? `${qs}&limit=5000` : '?limit=5000'}`, { headers: authJsonHeaders() }),
                fetch(`/api/depenses/bureau${qs}`, { headers: authJsonHeaders() }),
                fetch(`/api/caisse-cgm/retraits${qs}`, { headers: authJsonHeaders() }),
            ]);

            const honoData = await honoRes.json().catch(() => ({}));
            const depData = await depRes.json().catch(() => ({}));
            const retData = await retRes.json().catch(() => ({}));

            let totalHonoraires = 0;
            if (honoData.success && Array.isArray(honoData.honoraires)) {
                for (const h of honoData.honoraires) {
                    if (isHonoraireRecu(h.libelle)) {
                        const av = parseFloat(h.avance || 0);
                        const mt = parseFloat(h.montant || 0);
                        const val = Math.max(av, mt);
                        if (val > 0) totalHonoraires += val;
                    }
                }
            }

            let totalDepenses = 0;
            if (depData.success && Array.isArray(depData.depenses)) {
                for (const d of depData.depenses) {
                    const raw = (d.description || d.libelle || '').toUpperCase();
                    const isCarte = raw.includes('[CARTE BANCAIRE]') || raw.includes('[CARTE]');
                    if (isCarte) continue;
                    const isHon = isHonoraireRecu(raw);
                    const isAv = raw.includes('AVANCE');
                    if (!isHon && !isAv) {
                        totalDepenses += parseFloat(d.montant || 0);
                    }
                }
            }

            if (retData.success && Array.isArray(retData.retraits)) {
                for (const r of retData.retraits) {
                    if (!r.is_internal_expense && String(r.type_retrait).toLowerCase() !== 'bureau') {
                        totalDepenses += parseFloat(r.montant || 0);
                    }
                }
            }

            const resteCgm = totalHonoraires - totalDepenses;

            setSummary({
                honoraires: totalHonoraires,
                depenses: totalDepenses,
                reste: resteCgm,
                loading: false,
            });
        } catch (err) {
            console.error('Erreur lors du calcul du résumé État CGM:', err);
            setSummary(prev => ({ ...prev, loading: false }));
        }
    }, [tab, selectedYear]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary, activeCabinet?.id]);

    useEffect(() => {
        const handleCaisseUpdated = () => {
            fetchSummary();
        };
        window.addEventListener('caisse-updated', handleCaisseUpdated);
        window.addEventListener('focus', handleCaisseUpdated);
        return () => {
            window.removeEventListener('caisse-updated', handleCaisseUpdated);
            window.removeEventListener('focus', handleCaisseUpdated);
        };
    }, [fetchSummary]);

    const handleShowEtat = () => {
        window.currentEtatClientId = null;
        window.currentEtatType = 'bureau';
        setShowModal(true);
    };

    const buildEtatCgmPrintUrl = (preview = false) => {
        const params = new URLSearchParams();
        const fType = tab === 'jour' ? (jourMode === 'date' ? 'jour' : 'periode') : tab;
        if (fType === 'jour' && jourMode === 'date') {
            params.set('filterType', 'jour');
            if (selectedDate) params.set('date', selectedDate);
        } else if (fType === 'periode' && dateDebut && dateFin) {
            params.set('filterType', 'periode');
            params.set('dateDebut', dateDebut);
            params.set('dateFin', dateFin);
        } else if (tab === 'mois') {
            params.set('filterType', 'mois');
            params.set('mois', String(selectedMonth));
            params.set('annee', String(selectedYear));
        } else if (tab === 'annee') {
            params.set('filterType', 'annee');
            params.set('annee', String(selectedYear));
        }
        if (preview) params.set('preview', '1');
        return `/etat-cgm/print?${params.toString()}`;
    };

    const years = buildCaisseYearOptions();
    const modalFilterType = tab === 'jour' ? (jourMode === 'date' ? 'jour' : 'periode') : tab;

    return (
        <CgmModulePageLayout
            crumb="Comptabilité CGM · État"
            title={`État ${cabinetLabel}`}
            backTo="/"
        >
            <div className="cgm-list-toolbar">
                {/* Carte Synthèse / Reste de Caisse en direct (uniquement pour la vue par Année) */}
                {tab === 'annee' && (
                    <div className="cgm-filter-panel cgm-period-panel mb-3" style={{ background: '#ffffff', border: '1px solid #0f766e22' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                <i className="fas fa-chart-pie me-2" />
                                Synthèse · Reste de Caisse ({getPeriodLabel()})
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                Au {new Date().toLocaleDateString('fr-FR')}
                            </div>
                        </div>
                        <div className="etat-inventory">
                            <div className="etat-synth-cards">
                                <div className="etat-synth-card">
                                    <div className="etat-synth-card__label">Honoraires + caisse</div>
                                    <div className="etat-synth-card__value is-credit">
                                        {summary.loading ? '…' : formatMontant(summary.honoraires)}
                                    </div>
                                </div>
                                <div className="etat-synth-card">
                                    <div className="etat-synth-card__label">Dépenses</div>
                                    <div className="etat-synth-card__value is-debit">
                                        {summary.loading ? '…' : formatMontant(summary.depenses)}
                                    </div>
                                </div>
                                <div className="etat-synth-card">
                                    <div className="etat-synth-card__label">Reste CGM</div>
                                    <div className={`etat-synth-card__value ${summary.reste >= 0 ? 'is-reste-pos' : 'is-reste-neg'}`}>
                                        {summary.loading ? '…' : formatMontant(summary.reste)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="cgm-filter-panel cgm-period-panel">
                    <div className="cgm-filter-panel__title">Filtrer l&apos;état {cabinetLabel}</div>
                    <div className="cgm-period-body">
                        <div className="cgm-period-tabs">
                            <button type="button" className={`cgm-period-tab ${tab === 'jour' ? 'active' : ''}`} onClick={() => setTab('jour')}>Jour</button>
                            <button type="button" className={`cgm-period-tab ${tab === 'mois' ? 'active' : ''}`} onClick={() => setTab('mois')}>Mois</button>
                            <button type="button" className={`cgm-period-tab ${tab === 'annee' ? 'active' : ''}`} onClick={() => setTab('annee')}>Année</button>
                        </div>

                        {tab === 'jour' && (
                            <div className="cgm-period-jour-panel">
                                <div className="cgm-period-mode-group" role="group" aria-label="Mode jour">
                                    <div className="form-check">
                                        <input className="form-check-input" type="radio" id="etatBureauJourDate" name="etatBureauJourMode" checked={jourMode === 'date'} onChange={() => setJourMode('date')} />
                                        <label className="form-check-label" htmlFor="etatBureauJourDate">Par date</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="radio" id="etatBureauJourPeriode" name="etatBureauJourMode" checked={jourMode === 'periode'} onChange={() => setJourMode('periode')} />
                                        <label className="form-check-label" htmlFor="etatBureauJourPeriode">Par période</label>
                                    </div>
                                </div>
                                <div className="cgm-period-filter-row">
                                    {jourMode === 'date' ? (
                                        <>
                                            <label className="mb-0">Date</label>
                                            <input type="date" className="hono-input-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                                        </>
                                    ) : (
                                        <>
                                            <label className="mb-0">De</label>
                                            <input type="date" className="hono-input-date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                                            <label className="mb-0">à</label>
                                            <input type="date" className="hono-input-date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {tab === 'mois' && (
                            <div className="cgm-period-filter-row">
                                <label className="mb-0">Mois</label>
                                <select className="hono-select-month" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}>
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                        <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                    ))}
                                </select>
                                <label className="mb-0">Année</label>
                                <select className="hono-select-year" value={selectedYear} onChange={(e) => onYearChange(e.target.value)}>
                                    {years.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {tab === 'annee' && (
                            <div className="cgm-period-filter-row">
                                <label className="mb-0">Année</label>
                                <select className="hono-select-year" value={selectedYear} onChange={(e) => onYearChange(e.target.value)}>
                                    {years.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="cgm-period-actions">
                            <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary" onClick={handleShowEtat}>
                                Rechercher
                            </button>
                            <button
                                type="button"
                                className="cgm-toolbar-btn cgm-toolbar-btn--print"
                                onClick={async () => {
                                    await recordEtatCgmPrint({
                                        filterType: modalFilterType,
                                        date: tab === 'jour' && jourMode === 'date' ? selectedDate : null,
                                        dateDebut: tab === 'jour' && jourMode === 'periode' ? dateDebut : null,
                                        dateFin: tab === 'jour' && jourMode === 'periode' ? dateFin : null,
                                        mois: tab === 'mois' ? selectedMonth : null,
                                        annee: tab === 'mois' || tab === 'annee' ? selectedYear : null,
                                    });
                                    window.open(buildEtatCgmPrintUrl(), '_blank');
                                }}
                                title="Imprimer l'état CGM"
                            >
                                Imprimer
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showModal && (
                <EtatCgmModal
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    type="bureau"
                    filterType={modalFilterType}
                    date={tab === 'jour' && jourMode === 'date' ? selectedDate : null}
                    dateDebut={tab === 'jour' && jourMode === 'periode' ? dateDebut : null}
                    dateFin={tab === 'jour' && jourMode === 'periode' ? dateFin : null}
                    mois={tab === 'mois' ? selectedMonth : null}
                    annee={tab === 'mois' || tab === 'annee' ? selectedYear : null}
                />
            )}
        </CgmModulePageLayout>
    );
}

